import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { sendHeartbeat } from '../../api/eventApi';

const FONT = "'Poppins', sans-serif";

/**
 * "0 min · 8%" reads like a fault. Under a minute, say the seconds; the percentage is
 * against the whole video, so the two only look contradictory when the minutes have
 * been floored away. (user 2026-10-04: "watched 1min? number not tally")
 */
export const fmtWatched = (seconds) => {
    const s = Math.max(0, Math.round(seconds || 0));
    if (s < 60) return `${s} sec`;
    return `${Math.floor(s / 60)} min`;
};
const TICK_MS = 15000;
/** How far out of step with the clock the video may drift before it is pulled back. */
const DRIFT_TOLERANCE_S = 4;

/* ============================================================================
   SESSION PLAYER — a live session, not a video file

   The session runs to the clock and cannot be replayed, paused or skipped. The
   position is worked out from the session's start time, so everyone is at the same
   moment and a latecomer arrives part-way through, exactly as they would walking
   into a room. (user 2026-10-03: "cant replay. its live")

   How that is enforced, since a YouTube embed will happily let you scrub:
     · controls: 0 and disablekb: 1          — no scrub bar, no keyboard seeking
     · a transparent sheet over the iframe   — no click-through to YouTube's own UI,
                                               which is also where "Watch on YouTube"
                                               and the end-screen suggestions live
     · a drift check every second            — anything that does move the position
                                               gets pulled straight back to the clock
   So there is no way out to YouTube and no way to rewind. (user 2026-10-03: "close
   all youtube redirect")

   AUTOPLAY IS MUTED. Every browser blocks autoplay with sound until the viewer has
   interacted with the page, so the player starts muted with a button to turn the
   sound on. That is a browser rule, not a setting.

   ENGAGEMENT is measured here rather than taken from Zoom, which has not reported
   attendee attention since 2020 and gives join/leave duration only. Each tick posts
   the DELTA since the last one — never a running total — and the server clamps it,
   so the figures cannot be inflated from the browser.
   ========================================================================= */

function youTubeId(url) {
    if (!url) return null;
    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([\w-]{11})/,
        /^([\w-]{11})$/,
    ];
    for (const p of patterns) {
        const m = url.match(p);
        if (m) return m[1];
    }
    return null;
}

let apiPromise = null;
function loadYouTubeApi() {
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
    if (apiPromise) return apiPromise;
    apiPromise = new Promise((resolve) => {
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof prev === 'function') prev();
            resolve(window.YT);
        };
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
    });
    return apiPromise;
}

export default function SessionPlayer({ session, clockSynced, isLive, onProgress }) {
    const holderRef = useRef(null);
    const playerRef = useRef(null);
    const lastPosRef = useRef(null);
    const pendingRef = useRef({ delta: 0, focusedDelta: 0, position: 0 });
    const [muted, setMuted] = useState(true);
    const [ready, setReady] = useState(false);
    const [ended, setEnded] = useState(false);
    // Has the session's start time passed? Re-checked on a timer so the page flips to
    // live on its own, without anybody reloading.
    const [started, setStarted] = useState(
        () => !session?.startsOn || Date.now() >= new Date(session.startsOn).getTime(),
    );
    const [countdown, setCountdown] = useState('');
    const [stats, setStats] = useState({ watchedSeconds: 0, focusedSeconds: 0, completionPercent: 0, watchingNow: 0 });

    const videoId = youTubeId(session?.videoUrl);

    /** Where the session should be right now, measured from its start time. */
    const expectedPosition = useCallback(() => {
        if (!session?.startsOn) return 0;
        const elapsed = (Date.now() - new Date(session.startsOn).getTime()) / 1000;
        return elapsed > 0 ? Math.floor(elapsed) : 0;
    }, [session]);

    // Until the start time there is nothing to watch. Checked every second so the
    // room goes live by itself at the stroke of the hour.
    useEffect(() => {
        if (!session?.startsOn) { setStarted(true); return; }
        const tick = () => {
            const ms = new Date(session.startsOn).getTime() - Date.now();
            if (ms <= 0) { setStarted(true); setCountdown(''); return; }
            const mins = Math.floor(ms / 60000);
            const d = Math.floor(mins / 1440), h = Math.floor((mins % 1440) / 60), m = mins % 60;
            setCountdown(d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`);
        };
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [session?.startsOn]);

    // ── Build the player ────────────────────────────────────────────────────
    useEffect(() => {
        if (!videoId || !holderRef.current || !started) return;
        let destroyed = false;

        loadYouTubeApi().then((YT) => {
            if (destroyed || !holderRef.current) return;
            playerRef.current = new YT.Player(holderRef.current, {
                width: '100%',
                height: '100%',
                videoId,
                playerVars: {
                    autoplay: 1,
                    mute: 1,          // the only autoplay a browser will honour
                    controls: 0,      // no scrub bar, so no rewinding or skipping
                    disablekb: 1,     // and no seeking from the keyboard either
                    rel: 0,           // no "more videos" at the end
                    modestbranding: 1,
                    iv_load_policy: 3, // no annotation cards linking out
                    playsinline: 1,
                    fs: 0,            // the fullscreen chrome carries a YouTube link
                },
                events: {
                    onReady: (e) => {
                        setReady(true);
                        const duration = Math.floor(e.target.getDuration() || 0);
                        const offset = clockSynced ? expectedPosition() : 0;
                        if (duration > 0 && offset >= duration) {
                            // They arrived after it finished. A live session that has ended
                            // has ended — it is not a recording to start over, and it is not
                            // left playing behind the notice either.
                            try { e.target.stopVideo?.(); } catch { /* already gone */ }
                            setEnded(true);
                            return;
                        }
                        if (offset > 0) e.target.seekTo(offset, true);
                        e.target.playVideo();
                        lastPosRef.current = e.target.getCurrentTime() || 0;
                    },
                    onStateChange: (e) => {
                        // 0 = ended. A live session does not loop back to the start, and it
                        // must not be left running either - stopVideo so YouTube cannot roll
                        // on into anything of its own.
                        if (e.data === 0) {
                            try { e.target.stopVideo?.(); } catch { /* already gone */ }
                            setEnded(true);
                        }
                    },
                },
            });
        });

        return () => {
            destroyed = true;
            try { playerRef.current?.destroy?.(); } catch { /* already gone */ }
            playerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId, started, session?.eventSessionId]);

    // "This session has ended" used to be an overlay and nothing more: the video
    // carried on playing underneath it, and once YouTube reached the end it was free
    // to roll into whatever it wanted next. Ending the session now stops the player.
    // (user 2026-10-04: "ended but video ongoing? isit in loop?")
    useEffect(() => {
        if (!ended) return;
        const p = playerRef.current;
        try { p?.stopVideo?.(); } catch { /* already gone */ }
        try { p?.pauseVideo?.(); } catch { /* stopVideo is usually enough */ }
    }, [ended]);

    // ── Hold it on the clock, and accumulate watch time ─────────────────────
    useEffect(() => {
        if (!ready || ended || !started) return;

        const sampler = setInterval(() => {
            const p = playerRef.current;
            if (!p?.getCurrentTime) return;
            const pos = p.getCurrentTime() || 0;

            // Pull it back if it has drifted from where the clock says it should be —
            // a pause, a stall, or anything that managed to seek.
            if (clockSynced) {
                const want = expectedPosition();
                const duration = Math.floor(p.getDuration?.() || 0);
                // Past the end of the video: the session is over. Stop it here as well as
                // flipping the flag - this tick may land before the effect above runs.
                if (duration > 0 && want >= duration) {
                    try { p.stopVideo?.(); } catch { /* already gone */ }
                    setEnded(true);
                    return;
                }
                if (Math.abs(pos - want) > DRIFT_TOLERANCE_S) {
                    p.seekTo(want, true);
                    p.playVideo?.();
                    lastPosRef.current = want;
                    return;      // this jump is not watch time
                }
                if (p.getPlayerState?.() === 2) p.playVideo?.();   // 2 = paused
            }

            const prev = lastPosRef.current;
            lastPosRef.current = pos;
            if (prev == null) return;

            // Only forward movement of roughly real time counts as watching.
            const moved = pos - prev;
            if (moved <= 0 || moved > 3) {
                pendingRef.current.position = Math.floor(pos);
                return;
            }

            const focused = document.visibilityState === 'visible' && document.hasFocus();
            pendingRef.current.delta += moved;
            if (focused) pendingRef.current.focusedDelta += moved;
            pendingRef.current.position = Math.floor(pos);
        }, 1000);

        const poster = setInterval(async () => {
            const pending = pendingRef.current;
            const delta = Math.round(pending.delta);
            if (delta <= 0) return;

            const focusedShare = pending.delta > 0 ? pending.focusedDelta / pending.delta : 0;
            pendingRef.current = { delta: 0, focusedDelta: 0, position: pending.position };

            try {
                const res = await sendHeartbeat({
                    eventSessionId: session.eventSessionId,
                    deltaSeconds: delta,
                    positionSeconds: pending.position,
                    focused: focusedShare >= 0.5,
                    videoLengthSeconds: Math.floor(playerRef.current?.getDuration?.() || 0) || undefined,
                });
                setStats(res.data);
                onProgress?.(res.data);
            } catch {
                // A dropped tick loses at most one interval; the next carries on.
            }
        }, TICK_MS);

        return () => { clearInterval(sampler); clearInterval(poster); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, ended, started, clockSynced, session?.eventSessionId]);

    const unmute = () => {
        try {
            playerRef.current?.unMute?.();
            playerRef.current?.setVolume?.(100);
            setMuted(false);
        } catch { /* nothing to do */ }
    };

    if (!videoId) {
        return (
            <Box sx={{ p: 3, border: '1px dashed #CBD5E1', borderRadius: '12px', textAlign: 'center' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: '#64748B' }}>
                    No video has been set for this session yet.
                </Typography>
            </Box>
        );
    }

    if (!started) {
        // The bug this replaces: clock-sync put the position at "now minus start", which
        // is negative before a session begins, so it clamped to 0 - and the drift check
        // then yanked the video back to 0 every few seconds. It looked paused and recorded
        // nothing. A session that has not started does not play. (user 2026-10-03)
        return (
            <Box>
                <Box sx={{
                    width: '100%', borderRadius: '12px', bgcolor: '#0F172A', aspectRatio: '16 / 9',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
                }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.05rem', color: '#fff' }}>
                        Starts in {countdown || 'a moment'}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: '#94A3B8' }}>
                        {session?.startsOn && new Date(session.startsOn).toLocaleString('en-MY', {
                            weekday: 'long', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
                        })}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.76rem', color: '#64748B', mt: 1 }}>
                        It begins by itself — leave this open, or come back then.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: '#64748B' }}>
                        Nothing is counted as watched until it starts.
                    </Typography>
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{
                position: 'relative', width: '100%', borderRadius: '12px', overflow: 'hidden',
                bgcolor: '#000', aspectRatio: '16 / 9',
                '& iframe': { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 },
            }}>
                {/* The YouTube API REPLACES this div with its iframe, taking any styling
                    on it along too - which is why the sizing rule lives on the parent
                    above and not here, and why the player is told its size explicitly.
                    Without both, the iframe renders at YouTube's default 640x390 inside
                    the 16:9 box and the video sits letterboxed in the corner. */}
                <Box ref={holderRef} sx={{ position: 'absolute', inset: 0 }} />

                {/* A transparent sheet over the whole frame. Nothing underneath can be
                    clicked: not the player, not YouTube's title bar, not "Watch on
                    YouTube", not the end-screen cards. It is also why there is no pause
                    and no scrub. (user 2026-10-03) */}
                <Box sx={{ position: 'absolute', inset: 0, cursor: 'default' }} />

                {ended && (
                    <Box sx={{
                        position: 'absolute', inset: 0, bgcolor: 'rgba(15,23,42,.92)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
                    }}>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1rem', color: '#fff' }}>
                            This session has ended
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#CBD5E1' }}>
                            The chat stays open, and your questions still get answered.
                        </Typography>
                    </Box>
                )}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
                {muted && !ended && (
                    <Button size="small" variant="contained" startIcon={<VolumeOffIcon />} onClick={unmute}
                        sx={{ fontFamily: FONT, textTransform: 'none', fontSize: '0.74rem' }}>
                        Tap for sound
                    </Button>
                )}
                {!ended && (
                    <Chip label="LIVE" size="small"
                        sx={{ height: 20, fontSize: '0.64rem', fontWeight: 800, bgcolor: '#DC2626', color: '#fff' }} />
                )}
                {/* Who else is here. The number rides back on each heartbeat - anybody whose
                    last one was within the window is counted, so somebody who closes the tab
                    simply stops arriving and drops off. Hidden until the first tick, rather
                    than flashing a 0 that is never true while you yourself are watching.
                    (user 2026-10-04) */}
                {!ended && stats.watchingNow > 0 && (
                    <Chip
                        size="small"
                        icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                        label={`${stats.watchingNow} watching now`}
                        sx={{
                            height: 20, fontSize: '0.64rem', fontWeight: 700,
                            bgcolor: '#F1F5F9', color: '#334155',
                            '& .MuiChip-icon': { color: '#64748B', ml: 0.6 },
                        }} />
                )}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: '#64748B' }}>
                    Watched {fmtWatched(stats.watchedSeconds)}
                    {stats.completionPercent ? ` · ${stats.completionPercent}%` : ''}
                </Typography>
            </Stack>
        </Box>
    );
}
